import {
  addDays,
  addMilliseconds,
  differenceInMilliseconds,
  subDays,
  subMilliseconds,
} from 'date-fns';
import memoize from 'lodash/memoize';
import { LRUCache } from 'lru-cache';
import { toZonedTime } from 'date-fns-tz';
import { ET, isMarketOpenOnDateObj } from 'util/shared/date';

export const MS_IN_MKT_DAY: number = (16 - 9.5) * 60 * 60 * 1_000;
export const MS_BETWEEN_MKT_DAYS: number = 24 * 60 * 60 * 1_000 - MS_IN_MKT_DAY;

const mutateDate = (
  d: Date,
  hours: number,
  minutes = 0,
  seconds = 0,
  milliseconds = 0,
) => {
  d.setUTCHours(hours);
  d.setUTCMinutes(minutes);
  d.setSeconds(seconds);
  d.setMilliseconds(milliseconds);
  return d;
};

export const utcDateTupleToEtHoursOffset = memoize(
  (date: Date): number => {
    const utcDate = toZonedTime(date, 'UTC');
    const etDate = toZonedTime(date, ET);
    return (etDate.valueOf() - utcDate.valueOf()) / (60 * 60 * 1_000);
  },
  (date: Date) =>
    `${date.getUTCFullYear()}-${date.getUTCMonth()}-${date.getUTCDate()}`,
);

export const utcDateToEtMarketBegEndHours = (
  date: Date,
): {
  marketBeginHour: number;
  marketEndHour: number;
  easternOffset: number;
} => {
  const easternOffset = utcDateTupleToEtHoursOffset(date);
  return {
    marketBeginHour: 9 - easternOffset,
    marketEndHour: 16 - easternOffset,
    easternOffset,
  };
};

export const isMarketOpenOnUtcDate = (date: Date, easternOffset: number) => {
  const utcHours = date.getUTCHours();
  if (utcHours < -easternOffset) {
    return isMarketOpenOnDateObj(subDays(date, 1));
  } else {
    return isMarketOpenOnDateObj(date);
  }
};

// need to use an LRUCache rather than memoize, because these functions are
// called on every pixel in the chart that the mouse hits.
const distanceLRUcache = new LRUCache<bigint, number>({ max: 1_000 });
const offsetLRUcache = new LRUCache<bigint, Date>({ max: 1_000 });

export const skipNonMarketHours = {
  clampDown: (d: Date): Date => {
    if (d == null || isNaN(+d)) {
      return d;
    }
    d = new Date(d);
    const { marketBeginHour, marketEndHour, easternOffset } =
      utcDateToEtMarketBegEndHours(d);
    let h = d.getUTCHours();
    if (
      h < marketBeginHour ||
      (h == marketBeginHour && d.getUTCMinutes() < 30)
    ) {
      d = subDays(d, 1);
      mutateDate(d, marketEndHour);
    }
    while (!isMarketOpenOnUtcDate(d, easternOffset)) {
      d = subDays(d, 1);
      const { marketEndHour } = utcDateToEtMarketBegEndHours(d);
      mutateDate(d, marketEndHour);
    }
    h = d.getUTCHours();
    if (
      h > marketEndHour ||
      (h == marketEndHour &&
        (d.getUTCMinutes() > 0 ||
          d.getSeconds() > 0 ||
          d.getMilliseconds() > 0))
    ) {
      mutateDate(d, marketEndHour);
    }
    return d;
  },

  clampUp: (d: Date): Date => {
    if (d == null || isNaN(+d)) {
      return d;
    }
    d = new Date(d);
    // eslint-disable-next-line prefer-const
    let { marketBeginHour, marketEndHour, easternOffset } =
      utcDateToEtMarketBegEndHours(d);
    let offset = easternOffset;

    let h = d.getUTCHours();
    if (
      h > marketEndHour ||
      h < -easternOffset ||
      (h == marketEndHour &&
        (d.getUTCMinutes() > 0 ||
          d.getSeconds() > 0 ||
          d.getMilliseconds() > 0))
    ) {
      if (h > -easternOffset) {
        d = addDays(d, 1);
      }
      mutateDate(d, marketBeginHour);
    }
    offset = utcDateToEtMarketBegEndHours(d).easternOffset;
    while (!isMarketOpenOnUtcDate(d, offset)) {
      d = addDays(d, 1);
      const { marketBeginHour: beg, easternOffset } =
        utcDateToEtMarketBegEndHours(d);
      marketBeginHour = beg;
      offset = easternOffset;
      mutateDate(d, marketBeginHour, 30);
    }
    h = d.getUTCHours();
    if (
      h < marketBeginHour ||
      (h == marketBeginHour && d.getUTCMinutes() < 30)
    ) {
      mutateDate(d, marketBeginHour, 30);
    }
    return d;
  },

  // returns the number of included milliseconds (i.e. those which do not fall)
  // within discontinuities, along this scale
  distance: (startDate: Date, endDate: Date): number => {
    if (
      startDate == null ||
      isNaN(+startDate) ||
      endDate == null ||
      isNaN(+endDate)
    ) {
      return 0;
    }
    // construct a 64-bit integer from the two unix timestamps
    const lruKey =
      BigInt(Math.round(startDate.valueOf() / 1_000)) * BigInt(1 << 30) +
      BigInt(Math.round(endDate.valueOf() / 1_000));

    const cacheHit = distanceLRUcache.get(lruKey);
    if (cacheHit != null) {
      return cacheHit;
    }

    startDate = skipNonMarketHours.clampUp(startDate);
    endDate = skipNonMarketHours.clampDown(endDate);

    let result = 0;
    let d = startDate;
    while (d < endDate) {
      const nextD = addDays(d, 1);
      const { easternOffset } = utcDateToEtMarketBegEndHours(nextD);
      const nextIsOpen = isMarketOpenOnUtcDate(nextD, easternOffset);

      if (nextD > endDate) {
        result += differenceInMilliseconds(endDate, d);
        if (d.getUTCDate() != endDate.getUTCDate()) {
          result -= MS_BETWEEN_MKT_DAYS;
        }
      } else {
        if (nextIsOpen) {
          result += MS_IN_MKT_DAY;
        }
      }

      d = nextD;
    }
    //console.log('skipNonMarketHours.distance', startDate.toISOString(), endDate.toISOString(), result);
    distanceLRUcache.set(lruKey, result);
    return result;
  },

  offset: (startDate: Date, ms: number): Date => {
    if (startDate == null || isNaN(+startDate)) {
      return startDate;
    }
    // construct a 64-bit integer from the unix timestamp and `ms` number
    const lruKey =
      BigInt(Math.round(startDate.valueOf() / 1_000)) * BigInt(1 << 30) +
      BigInt(Math.round(ms));
    const cacheHit = offsetLRUcache.get(lruKey);
    if (cacheHit != null) {
      return new Date(cacheHit);
    }
    let result = startDate;
    //console.log('skipNonMarketHours.offset', startDate.toISOString(), ms);
    let d = skipNonMarketHours.clampUp(startDate);
    if (ms === 0) {
      result = d;
    } else if (ms > 0) {
      while (ms > MS_IN_MKT_DAY) {
        do {
          d = addDays(d, 1);
        } while (
          !isMarketOpenOnUtcDate(
            d,
            utcDateToEtMarketBegEndHours(d).easternOffset,
          )
        );
        ms -= MS_IN_MKT_DAY;
      }
      const { marketEndHour } = utcDateToEtMarketBegEndHours(d);
      const eod = mutateDate(new Date(d), marketEndHour);
      const msToEOD = differenceInMilliseconds(eod, d);
      if (msToEOD >= ms) {
        result = addMilliseconds(d, ms);
      } else {
        ms -= msToEOD;
        do {
          d = addDays(d, 1);
        } while (
          !isMarketOpenOnUtcDate(
            d,
            utcDateToEtMarketBegEndHours(d).easternOffset,
          )
        );
        const { marketBeginHour } = utcDateToEtMarketBegEndHours(d);
        result = addMilliseconds(mutateDate(d, marketBeginHour, 30), ms);
      }
    } else if (ms < 0) {
      ms = -ms;
      while (ms > MS_IN_MKT_DAY) {
        do {
          d = subDays(d, 1);
        } while (
          !isMarketOpenOnUtcDate(
            d,
            utcDateToEtMarketBegEndHours(d).easternOffset,
          )
        );
        ms -= MS_IN_MKT_DAY;
      }
      const { marketBeginHour } = utcDateToEtMarketBegEndHours(d);
      const bod = mutateDate(new Date(d), marketBeginHour, 30);
      const msToBOD = differenceInMilliseconds(d, bod);
      if (msToBOD >= ms) {
        result = subMilliseconds(d, ms);
      } else {
        ms -= msToBOD;
        do {
          d = subDays(d, 1);
        } while (
          !isMarketOpenOnUtcDate(
            d,
            utcDateToEtMarketBegEndHours(d).easternOffset,
          )
        );
        const { marketEndHour } = utcDateToEtMarketBegEndHours(d);
        result = subMilliseconds(mutateDate(d, marketEndHour), ms);
      }
    }

    offsetLRUcache.set(lruKey, result);
    return new Date(result);
  },

  copy: () => {
    return skipNonMarketHours;
  },

  dateInMarketHours: (d: Date): boolean => {
    if (
      !isMarketOpenOnUtcDate(d, utcDateToEtMarketBegEndHours(d).easternOffset)
    ) {
      return false;
    }
    const { marketBeginHour, marketEndHour } = utcDateToEtMarketBegEndHours(d);
    const h = d.getUTCHours();
    return !(
      h < marketBeginHour ||
      (h == marketBeginHour && d.getUTCMinutes() < 30) ||
      h > marketEndHour ||
      (h == marketEndHour &&
        (d.getUTCMinutes() > 0 ||
          d.getSeconds() > 0 ||
          d.getMilliseconds() > 0))
    );
  },
};
